import React from 'react';
import OfficeBuildingFilledIcon from '@atlaskit/icon/core/migration/office-building--office-building-filled';
import PeopleIcon from '@atlaskit/icon/core/migration/people-group--people';
import { SpotlightTransition, SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { useSettingFeatures } from '@atlassian/jira-navigation-apps-sidebar-servicedesk/src/controllers/features/main.tsx';
import { useSeenStatus } from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/controllers/seen-status/index.tsx';
import { scimTeamName, packageName } from '../../constants.tsx';
import { messages } from '../../messages.tsx';
import ItemOrganizationsSpotlight from './item-organizations-spotlight/index.tsx';
import type { CustomerSupportManagementSectionProps } from './types.tsx';

// Do not change this ID, as changing it will cause the spotlight to reappear for customers (Bad UX).
const NAVIGATION_NEW_ITEM_ORGANIZATIONS_CHANGEBOARDING_ITEM_KEY =
	'navigation-new-item-organizations-changeboarding-seen-status';

const useThrowSettingFeatures = withThrowOnError(useSettingFeatures);

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/jsm-project-menu
 */
export const CustomerSupportManagementItems = ({
	urlPath,
	// currently not used but can be in the future for premium/non-premium features
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	isPremiumTenant,
}: CustomerSupportManagementSectionProps) => {
	const { formatMessage } = useIntl();
	const { data: settingFeatures } = useThrowSettingFeatures();
	const isCustomerSupportManagementEnabled = Boolean(settingFeatures?.hasCustomerSupportManagement);
	const [{ isSeen: hasSeenSpotlight }, { markAsSeen: markSpotlightAsSeen }] = useSeenStatus({
		userPropertyKey: NAVIGATION_NEW_ITEM_ORGANIZATIONS_CHANGEBOARDING_ITEM_KEY,
		isSeenValue: true,
		packageName,
		teamName: scimTeamName,
	});

	return (
		<>
			{fg('jsm_customers_directory_page_change_boarding') ? (
				<SpotlightManager>
					<SpotlightTarget name="organizations">
						<MenuItem
							analytics={{ itemId: 'organizations' }}
							href={`${urlPath}/organizations`}
							iconBefore={<OfficeBuildingFilledIcon spacing="spacious" label="" />}
							selectedOn={[
								'servicedesk-organizations',
								'servicedesk-organization-details',
								'servicedesk-organization_v2',
							]}
						>
							{formatMessage(messages.itemOrganizations)}
						</MenuItem>
					</SpotlightTarget>
					{!hasSeenSpotlight && !isCustomerSupportManagementEnabled && (
						<SpotlightTransition>
							<ItemOrganizationsSpotlight onAcknowledge={markSpotlightAsSeen} />
						</SpotlightTransition>
					)}
				</SpotlightManager>
			) : (
				<MenuItem
					analytics={{ itemId: 'organizations' }}
					href={`${urlPath}/organizations`}
					iconBefore={<OfficeBuildingFilledIcon spacing="spacious" label="" />}
					selectedOn={[
						'servicedesk-organizations',
						'servicedesk-organization-details',
						'servicedesk-organization_v2',
					]}
				>
					{formatMessage(messages.itemOrganizations)}
				</MenuItem>
			)}
			<MenuItem
				analytics={{ itemId: 'customers' }}
				href={`${urlPath}/customers`}
				iconBefore={<PeopleIcon spacing="spacious" label="" />}
				selectedOn={['servicedesk-customers-v2']}
			>
				{formatMessage(messages.itemIndividuals)}
			</MenuItem>
		</>
	);
};
